import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Text,
  Grid,
  Heading,
  Button,
  useColorModeValue,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  IconButton,
  Avatar,
  Tooltip,
  Checkbox,
  Tabs,
  TabList,
  Tab,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
} from "@chakra-ui/react";
import ReactSelect from "react-select";
import { HiOutlineRefresh } from "react-icons/hi";
import { FiEdit2, FiUserPlus, FiMapPin, FiGrid, FiSearch, FiFileText, FiCheckCircle, FiClock, FiAward, FiLoader, FiUser } from "react-icons/fi";
import Pagination from "components/common/Pagination/Pagination";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import POComponent from "App/BsesPoc/components/POComponent/POComponent";
import { formatDateTime } from "utils/dateUtils/dateUtils";

interface MiroDocument {
  _id: string;
  documentId?: string;
  status: string;
  poExtractionType: string;
  processType?: string;
  processName?: string;
  updated_At: string;
  file?: any;
  values?: any[];
  originalValues?: Record<string, any>;
  miroAssignUser?: any;
  plantCode?: string;
  purchaseGroup?: string;
}

export default function MiroAdminTable(): JSX.Element {
  const { toastNotify, workFlowData } = useContext(AppContext);
  const [data, setData] = useState<MiroDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [plantCode, setPlantCode] = useState<string>("");
  const [purchaseGroup, setPurchaseGroup] = useState<string>("");
  const [selectedAssigneeFilter, setSelectedAssigneeFilter] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const pageSize = 10;

  // Selected row state for POComponent drawer
  const [selectedData, setSelectedData] = useState<MiroDocument | null>(null);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  // Assign Modal state
  const { isOpen: isAssignOpen, onOpen: onAssignOpen, onClose: onAssignClose } = useDisclosure();
  const [assigningDocIds, setAssigningDocIds] = useState<string[]>([]);
  const [miroUsers, setMiroUsers] = useState<any[]>([]);
  const [allMiroUsers, setAllMiroUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [assigningUser, setAssigningUser] = useState<boolean>(false);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [assignmentStatus, setAssignmentStatus] = useState<"all" | "assigned" | "unassigned" | "approved" | "pending">("all");
  const [docCounts, setDocCounts] = useState<{ total: number; approved: number; pending: number; assigned: number; unassigned: number }>({
    total: 0,
    approved: 0,
    pending: 0,
    assigned: 0,
    unassigned: 0,
  });

  const openAssignModal = async (e: React.MouseEvent, docIds: string[], processType?: string, processName?: string) => {
    e.stopPropagation();
    setAssigningDocIds(docIds);
    setSelectedUserId("");
    setMiroUsers([]); // Clear previous users
    onAssignOpen();
    try {
      const res: any = await appRequest("poMiroUser", "getAll", { processType, processName });
      if (res?.status === "success" && Array.isArray(res.data)) {
        setMiroUsers(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch filtered MIRO users", err);
    }
  };

  const handleAssignUser = async () => {
    if (!selectedUserId || assigningDocIds.length === 0) return;
    try {
      setAssigningUser(true);
      const promises = assigningDocIds.map((docId) => 
        appRequest("document", "assignMiroUser", {
          documentId: docId,
          userId: selectedUserId
        })
      );
      await Promise.all(promises);
      toastNotify({ title: "Users assigned", type: "success" });
      fetchMiroDocuments();
      setSelectedDocs([]); // Clear selection
      onAssignClose();
    } catch (e: any) {
      toastNotify({ title: "Failed to assign some/all", message: e?.message, type: "error" });
    } finally {
      setAssigningUser(false);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedDocs(data.map(item => item._id));
    } else {
      setSelectedDocs([]);
    }
  };

  const handleSelectRow = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    if (e.target.checked) {
      setSelectedDocs([...selectedDocs, id]);
    } else {
      setSelectedDocs(selectedDocs.filter(docId => docId !== id));
    }
  };

  // Define standard PO fields we should display as columns (with aliases)
  const columnsToShow = useMemo(() => [
    { key: "PO_Number", label: "PO Number", aliases: ["Purchase_Order_Number", "Po_Number", "PO_Number"] },
    { key: "PO_Date", label: "PO Date", aliases: ["Purchase_Order_Date", "Po_Date", "PO_Date"] },
    { key: "Buyer_Name", label: "Buyer Name", aliases: ["Buyer_Name", "PO_Buyer_Name"] },
    { key: "Vendor_Name", label: "Vendor Name", aliases: ["Vendor_Name", "PO_Vendor_Name"] },
    { key: "Vendor_Code", label: "Vendor Code", aliases: ["Vender_Code", "Vendor_Code"] },
    { key: "Invoice_Number", label: "Invoice Number", aliases: ["Invoice_Number", "invoiceNo"] },
    { key: "Invoice_Date", label: "Invoice Date", aliases: ["Invoice_Date", "invoiceDate"] },
    { key: "Grand_Total", label: "Grand Total", aliases: ["Grand_Total", "Invoice_Amount", "invoiceAmount"] },
    { key: "Currency", label: "Currency", aliases: ["Currency", "PO_Currency"] },
  ], []);

  // Dynamically filter which columns to show based on what exists in originalValues
  const dynamicColumns = useMemo(() => {
    return columnsToShow.filter((col) => {
      return data.some((item) => {
        const orig = item.originalValues || {};
        return col.aliases.some((alias) => orig[alias] !== undefined && orig[alias] !== null);
      });
    });
  }, [data, columnsToShow]);

  const fetchMiroDocuments = async (page: number = currentPage, searchValue: string = search) => {
    try {
      setLoading(true);
      const response: any = await appRequest("document", "getMiroDocuments", {
        page: page,
        limit: pageSize,
        value: searchValue,
        processType: workFlowData?.processType,
        processName: workFlowData?.processName,
        plantCode: plantCode,
        purchaseGroup: purchaseGroup,
        assigneeId: selectedAssigneeFilter && selectedAssigneeFilter.length > 0 ? selectedAssigneeFilter.map((item: any) => item.value) : undefined,
        assignmentStatus: assignmentStatus !== "all" ? assignmentStatus : undefined
      });
      if (response && response.status === "success" && Array.isArray(response.data)) {
        setData(response.data);
        setTotalPages(response.totalPages || 1);
      } else {
        setData([]);
        setTotalPages(1);
        toastNotify({
          title: "Error fetching documents",
          message: response?.message || "Invalid response format.",
          type: "error",
          duration: 3000,
        });
      }
    } catch (error: any) {
      toastNotify({
        title: "Error fetching documents",
        message: error?.message || "Something went wrong.",
        type: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset page to 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const fetchCounts = async () => {
    try {
      const res: any = await appRequest("document", "getMiroDocumentsCount", {
        processType: workFlowData?.processType,
        processName: workFlowData?.processName,
        plantCode: plantCode,
        purchaseGroup: purchaseGroup,
        assigneeId: selectedAssigneeFilter && selectedAssigneeFilter.length > 0 ? selectedAssigneeFilter.map((item: any) => item.value) : undefined
      });
      if (res?.status === "success") {
        setDocCounts(res.data);
      }
    } catch (e) {
      console.error("Failed to fetch counts", e);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, [plantCode, purchaseGroup, workFlowData?.processType, workFlowData?.processName, selectedAssigneeFilter]);

  // Fetch when page, search query, or workflow data changes with a debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchMiroDocuments(currentPage, search);
    }, 600);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, search, plantCode, purchaseGroup, workFlowData?.processType, workFlowData?.processName, assignmentStatus, selectedAssigneeFilter]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const res: any = await appRequest("poMiroUser", "getAll", { 
          processType: workFlowData?.processType, 
          processName: workFlowData?.processName 
        });
        if (res?.status === "success" && Array.isArray(res.data)) {
          setAllMiroUsers(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch all MIRO users", err);
      }
    };
    fetchAllUsers();
  }, [workFlowData?.processType, workFlowData?.processName]);

  const handlePageChange = (page: any) => {
    setCurrentPage(page.selected + 1);
  };

  // Dynamic Heading based on selected card
  const getDynamicHeading = () => {
    switch(assignmentStatus) {
      case "all": return "All Documents";
      case "approved": return "Approved Documents";
      case "pending": return "Pending Documents";
      case "assigned": return "Assigned Documents";
      case "unassigned": return "Not Assigned Documents";
      default: return "Miro Admin Documents";
    }
  };

  const cardBg = useColorModeValue("white", "gray.800");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const rowHoverBg = useColorModeValue("gray.50", "gray.750");
  const rowText500Color = useColorModeValue("gray.800", "gray.100");

  // Pre-calculate border colors to avoid conditional hook calls
  const activeBlueBorder = useColorModeValue("blue.400", "blue.300");
  const activePurpleBorder = useColorModeValue("purple.400", "purple.300");
  const activeYellowBorder = useColorModeValue("yellow.500", "yellow.400");
  const activeGreenBorder = useColorModeValue("green.400", "green.300");
  const activeRedBorder = useColorModeValue("red.400", "red.300");

  return (
    <>
      <Box mt="1rem" p="1.5rem" bg={cardBg} borderRadius="xl" boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)">

        {/* Premium Count Cards */}
        <SimpleGrid columns={{ base: 1, md: 3, lg: 5 }} spacing="1.5rem" mb="1.5rem">
          
          {/* Total Documents Card */}
          <Box 
            p="1.5rem" 
            bg={useColorModeValue("linear-gradient(135deg, #EBF8FF 0%, #BEE3F8 100%)", "gray.700")} 
            borderRadius="xl" 
            boxShadow={assignmentStatus === "all" ? "0px 4px 20px rgba(66, 153, 225, 0.4)" : "sm"}
            border={assignmentStatus === "all" ? "3px solid" : "3px solid"}
            borderColor={assignmentStatus === "all" ? activeBlueBorder : "transparent"}
            cursor="pointer"
            onClick={() => { setAssignmentStatus("all"); setCurrentPage(1); }}
            transform={assignmentStatus === "all" ? "translateY(-4px)" : "none"}
            _hover={{ transform: "translateY(-4px)", boxShadow: "0px 4px 20px rgba(66, 153, 225, 0.4)" }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          >
            <Flex align="center" justify="space-between">
              <Stat>
                <StatLabel fontSize="sm" fontWeight="600" color={useColorModeValue("blue.700", "blue.200")} mb="0.25rem" textTransform="uppercase" letterSpacing="wider">
                  Total
                </StatLabel>
                <StatNumber fontSize="3xl" fontWeight="800" color={useColorModeValue("blue.900", "blue.100")}>
                  {docCounts.total}
                </StatNumber>
              </Stat>
              <Box p="0.8rem" bg={useColorModeValue("whiteAlpha.700", "whiteAlpha.200")} borderRadius="full" boxShadow="sm">
                <Icon as={FiFileText} w={6} h={6} color={useColorModeValue("blue.600", "blue.300")} />
              </Box>
            </Flex>
          </Box>

          {/* Approved Card */}
          <Box 
            p="1.5rem" 
            bg={useColorModeValue("linear-gradient(135deg, #E9D8FD 0%, #D6BCFA 100%)", "purple.700")} 
            borderRadius="xl" 
            boxShadow={assignmentStatus === "approved" ? "0px 4px 20px rgba(159, 122, 234, 0.4)" : "sm"}
            border={assignmentStatus === "approved" ? "3px solid" : "3px solid"}
            borderColor={assignmentStatus === "approved" ? activePurpleBorder : "transparent"}
            cursor="pointer"
            onClick={() => { setAssignmentStatus("approved"); setCurrentPage(1); }}
            transform={assignmentStatus === "approved" ? "translateY(-4px)" : "none"}
            _hover={{ transform: "translateY(-4px)", boxShadow: "0px 4px 20px rgba(159, 122, 234, 0.4)" }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          >
            <Flex align="center" justify="space-between">
              <Stat>
                <StatLabel fontSize="sm" fontWeight="600" color={useColorModeValue("purple.700", "purple.200")} mb="0.25rem" textTransform="uppercase" letterSpacing="wider">
                  Approved
                </StatLabel>
                <StatNumber fontSize="3xl" fontWeight="800" color={useColorModeValue("purple.900", "purple.100")}>
                  {docCounts.approved}
                </StatNumber>
              </Stat>
              <Box p="0.8rem" bg={useColorModeValue("whiteAlpha.700", "whiteAlpha.200")} borderRadius="full" boxShadow="sm">
                <Icon as={FiAward} w={6} h={6} color={useColorModeValue("purple.600", "purple.300")} />
              </Box>
            </Flex>
          </Box>

          {/* Pending Card */}
          <Box 
            p="1.5rem" 
            bg={useColorModeValue("linear-gradient(135deg, #FEFCBF 0%, #FAF089 100%)", "yellow.700")} 
            borderRadius="xl" 
            boxShadow={assignmentStatus === "pending" ? "0px 4px 20px rgba(236, 201, 75, 0.4)" : "sm"}
            border={assignmentStatus === "pending" ? "3px solid" : "3px solid"}
            borderColor={assignmentStatus === "pending" ? activeYellowBorder : "transparent"}
            cursor="pointer"
            onClick={() => { setAssignmentStatus("pending"); setCurrentPage(1); }}
            transform={assignmentStatus === "pending" ? "translateY(-4px)" : "none"}
            _hover={{ transform: "translateY(-4px)", boxShadow: "0px 4px 20px rgba(236, 201, 75, 0.4)" }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          >
            <Flex align="center" justify="space-between">
              <Stat>
                <StatLabel fontSize="sm" fontWeight="600" color={useColorModeValue("yellow.700", "yellow.200")} mb="0.25rem" textTransform="uppercase" letterSpacing="wider">
                  Pending
                </StatLabel>
                <StatNumber fontSize="3xl" fontWeight="800" color={useColorModeValue("yellow.900", "yellow.100")}>
                  {docCounts.pending}
                </StatNumber>
              </Stat>
              <Box p="0.8rem" bg={useColorModeValue("whiteAlpha.700", "whiteAlpha.200")} borderRadius="full" boxShadow="sm">
                <Icon as={FiLoader} w={6} h={6} color={useColorModeValue("yellow.600", "yellow.300")} />
              </Box>
            </Flex>
          </Box>

          {/* Assigned Card */}
          <Box 
            p="1.5rem" 
            bg={useColorModeValue("linear-gradient(135deg, #F0FFF4 0%, #C6F6D5 100%)", "gray.700")} 
            borderRadius="xl" 
            boxShadow={assignmentStatus === "assigned" ? "0px 4px 20px rgba(72, 187, 120, 0.4)" : "sm"}
            border={assignmentStatus === "assigned" ? "3px solid" : "3px solid"}
            borderColor={assignmentStatus === "assigned" ? activeGreenBorder : "transparent"}
            cursor="pointer"
            onClick={() => { setAssignmentStatus("assigned"); setCurrentPage(1); }}
            transform={assignmentStatus === "assigned" ? "translateY(-4px)" : "none"}
            _hover={{ transform: "translateY(-4px)", boxShadow: "0px 4px 20px rgba(72, 187, 120, 0.4)" }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          >
            <Flex align="center" justify="space-between">
              <Stat>
                <StatLabel fontSize="sm" fontWeight="600" color={useColorModeValue("green.700", "green.200")} mb="0.25rem" textTransform="uppercase" letterSpacing="wider">
                  Assigned
                </StatLabel>
                <StatNumber fontSize="3xl" fontWeight="800" color={useColorModeValue("green.900", "green.100")}>
                  {docCounts.assigned}
                </StatNumber>
              </Stat>
              <Box p="0.8rem" bg={useColorModeValue("whiteAlpha.700", "whiteAlpha.200")} borderRadius="full" boxShadow="sm">
                <Icon as={FiCheckCircle} w={6} h={6} color={useColorModeValue("green.600", "green.300")} />
              </Box>
            </Flex>
          </Box>

          {/* Not Assigned Card */}
          <Box 
            p="1.5rem" 
            bg={useColorModeValue("linear-gradient(135deg, #FFF5F5 0%, #FED7D7 100%)", "gray.700")} 
            borderRadius="xl" 
            boxShadow={assignmentStatus === "unassigned" ? "0px 4px 20px rgba(245, 101, 101, 0.4)" : "sm"}
            border={assignmentStatus === "unassigned" ? "3px solid" : "3px solid"}
            borderColor={assignmentStatus === "unassigned" ? activeRedBorder : "transparent"}
            cursor="pointer"
            onClick={() => { setAssignmentStatus("unassigned"); setCurrentPage(1); }}
            transform={assignmentStatus === "unassigned" ? "translateY(-4px)" : "none"}
            _hover={{ transform: "translateY(-4px)", boxShadow: "0px 4px 20px rgba(245, 101, 101, 0.4)" }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          >
            <Flex align="center" justify="space-between">
              <Stat>
                <StatLabel fontSize="sm" fontWeight="600" color={useColorModeValue("red.700", "red.200")} mb="0.25rem" textTransform="uppercase" letterSpacing="wider">
                  Not Assigned
                </StatLabel>
                <StatNumber fontSize="3xl" fontWeight="800" color={useColorModeValue("red.900", "red.100")}>
                  {docCounts.unassigned}
                </StatNumber>
              </Stat>
              <Box p="0.8rem" bg={useColorModeValue("whiteAlpha.700", "whiteAlpha.200")} borderRadius="full" boxShadow="sm">
                <Icon as={FiClock} w={6} h={6} color={useColorModeValue("red.600", "red.300")} />
              </Box>
            </Flex>
          </Box>

        </SimpleGrid>

        {/* Toolbar Area */}
        <Box mb="1.5rem">
          <Flex direction={{ base: "column", xl: "row" }} align={{ base: "start", xl: "center" }} justify="space-between" gap="1.5rem">
            
            <Heading 
              fontSize={{ base: "1.25rem", lg: "1.75rem" }} 
              fontWeight="800" 
              bgGradient={useColorModeValue("linear(to-r, teal.600, blue.600)", "linear(to-r, teal.300, blue.300)")}
              bgClip="text"
              letterSpacing="tight"
              display="flex"
              alignItems="center"
              gap="0.5rem"
            >
              {getDynamicHeading()}
            </Heading>

            <Flex gap="0.75rem" alignItems="center" flexWrap="wrap" justify={{ base: "flex-start", xl: "flex-end" }}>
            <Box w={{ base: "100%", md: "11rem" }}>
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiMapPin} color="teal.500" />
                </InputLeftElement>
                <Input 
                  placeholder="Plant Code" 
                  value={plantCode} 
                  onChange={(e) => setPlantCode(e.target.value)} 
                  bg={useColorModeValue("white", "gray.800")}
                  borderColor={useColorModeValue("gray.200", "gray.600")}
                  focusBorderColor="teal.400"
                  borderRadius="full"
                  boxShadow="sm"
                  _hover={{ boxShadow: "md", borderColor: "teal.300" }}
                  transition="all 0.2s"
                />
              </InputGroup>
            </Box>
            <Box w={{ base: "100%", md: "12rem" }}>
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiGrid} color="teal.500" />
                </InputLeftElement>
                <Input 
                  placeholder="Purchase Group" 
                  value={purchaseGroup} 
                  onChange={(e) => setPurchaseGroup(e.target.value)} 
                  bg={useColorModeValue("white", "gray.800")}
                  borderColor={useColorModeValue("gray.200", "gray.600")}
                  focusBorderColor="teal.400"
                  borderRadius="full"
                  boxShadow="sm"
                  _hover={{ boxShadow: "md", borderColor: "teal.300" }}
                  transition="all 0.2s"
                />
              </InputGroup>
            </Box>
            <Box w={{ base: "100%", md: "20rem" }}>
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none" h="100%">
                  <Icon as={FiUserPlus} color="teal.500" />
                </InputLeftElement>
                <Box w="100%" borderRadius="full">
                  <ReactSelect
                    isMulti
                    placeholder="All Assignees"
                    value={selectedAssigneeFilter}
                    onChange={(selected: any) => setSelectedAssigneeFilter(selected || [])}
                    options={allMiroUsers.map(u => ({ value: u.userId, label: u.userName || u.userId }))}
                    styles={{
                      control: (base: any) => ({
                        ...base,
                        borderRadius: "9999px",
                        minHeight: "40px",
                        borderColor: "#E2E8F0", // matches gray.200
                        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                        paddingLeft: "2.2rem",
                        flexWrap: "nowrap"
                      } as any),
                      valueContainer: (base: any) => ({
                        ...base,
                        flexWrap: "nowrap",
                        overflowX: "auto",
                        css: {
                          "&::-webkit-scrollbar": { display: "none" },
                          scrollbarWidth: "none"
                        }
                      } as any),
                      multiValue: (base: any) => ({
                        ...base,
                        minWidth: "fit-content"
                      } as any),
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 } as any)
                    }}
                    menuPortalTarget={document.body}
                  />
                </Box>
              </InputGroup>
            </Box>
            <Box w={{ base: "100%", md: "16rem" }}>
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="teal.500" />
                </InputLeftElement>
                <Input 
                  placeholder="Search..." 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                  bg={useColorModeValue("white", "gray.800")}
                  borderColor={useColorModeValue("gray.200", "gray.600")}
                  focusBorderColor="teal.400"
                  borderRadius="full"
                  boxShadow="sm"
                  _hover={{ boxShadow: "md", borderColor: "teal.300" }}
                  transition="all 0.2s"
                />
              </InputGroup>
            </Box>
            {selectedDocs.length > 0 && (
              <Button
                leftIcon={<FiUserPlus />}
                colorScheme="teal"
                borderRadius="lg"
                onClick={(e) => {
                  const firstSelected = data.find(d => d._id === selectedDocs[0]);
                  openAssignModal(e, selectedDocs, firstSelected?.processType, firstSelected?.processName);
                }}
                size="md"
                fontWeight="600"
                boxShadow="sm"
                _hover={{ boxShadow: "md", transform: "translateY(-1px)" }}
                transition="all 0.2s"
              >
                Assign Selected ({selectedDocs.length})
              </Button>
            )}
            <Button
              leftIcon={<HiOutlineRefresh />}
              colorScheme="teal"
              variant="solid"
              borderRadius="full"
              boxShadow="sm"
              _hover={{ boxShadow: "md", transform: "translateY(-1px)" }}
              onClick={() => {
                setSearch("");
                setPlantCode("");
                setPurchaseGroup("");
                setSelectedAssigneeFilter([]);
                setCurrentPage(1);
              }}
              size="md"
              fontWeight="600"
              transition="all 0.2s"
            >
              Refresh
            </Button>
            </Flex>
          </Flex>
        </Box>

        {/* Document Table */}
        <Box position="relative" overflow="auto" borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <Table variant="simple">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th w="40px" py="1rem">
                  <Checkbox 
                    colorScheme="teal" 
                    isChecked={data.length > 0 && selectedDocs.length === data.length}
                    isIndeterminate={selectedDocs.length > 0 && selectedDocs.length < data.length}
                    onChange={handleSelectAll}
                  />
                </Th>
                <Th color="gray.500" py="1rem" fontWeight="600" fontSize="xs">Document</Th>
                <Th color="gray.500" py="1rem" fontWeight="600" fontSize="xs" whiteSpace="nowrap">Assigned User</Th>
                {dynamicColumns.map((col) => (
                  <Th key={col.key} color="gray.500" py="1rem" fontWeight="600" fontSize="xs" whiteSpace="nowrap">
                    {col.label}
                  </Th>
                ))}
                <Th color="gray.500" py="1rem" fontWeight="600" fontSize="xs" whiteSpace="nowrap">Plant Code</Th>
                <Th color="gray.500" py="1rem" fontWeight="600" fontSize="xs" whiteSpace="nowrap">Purchase Group</Th>
                <Th color="gray.500" py="1rem" fontWeight="600" fontSize="xs">Last Updated</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={dynamicColumns.length + 2} py="4rem">
                    <Flex justify="center" align="center" direction="column" gap="1rem">
                      <Spinner color="teal.500" size="lg" thickness="3px" />
                      <Text color="gray.500" fontSize="sm">Loading Miro documents...</Text>
                    </Flex>
                  </Td>
                </Tr>
              ) : data.length === 0 ? (
                <Tr>
                  <Td colSpan={dynamicColumns.length + 2} py="4rem">
                    <Center flexDirection="column">
                      <Text fontSize="md" fontWeight="500" color="gray.400">
                        No Miro documents found
                      </Text>
                      {search && (
                        <Text fontSize="xs" color="gray.400" mt="0.25rem">
                          Try adjusting your search criteria
                        </Text>
                      )}
                    </Center>
                  </Td>
                </Tr>
              ) : (
                data.map((item) => {
                  const statusVal = item.status || "pending";
                  let badgeColorScheme = "orange";
                  if (statusVal === "approved") badgeColorScheme = "green";
                  else if (statusVal === "rejected") badgeColorScheme = "red";

                  return (
                    <Tr
                      key={item._id}
                      onClick={() => {
                        setSelectedData(item);
                        setSelectedFile(item.file);
                        setIsDrawerOpen(true);
                      }}
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{
                        bg: rowHoverBg,
                        transform: "translateY(-1px)",
                        boxShadow: "sm",
                      }}
                    >
                      <Td w="40px" py="1.25rem" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          colorScheme="teal"
                          isChecked={selectedDocs.includes(item._id)}
                          onChange={(e) => handleSelectRow(e, item._id)}
                        />
                      </Td>
                      <Td fontWeight="500" py="1.25rem" color={rowText500Color} whiteSpace="nowrap">
                        {item.documentId || "N/A"}
                      </Td>
                      <Td whiteSpace="nowrap" onClick={(e) => e.stopPropagation()}>
                        {item.miroAssignUser ? (
                          <Flex align="center" gap="0.75rem">
                            <Badge 
                              colorScheme="teal" 
                              variant="subtle" 
                              px="0.75rem" 
                              py="0.35rem" 
                              borderRadius="full" 
                              textTransform="none" 
                              fontWeight="600" 
                              fontSize="xs"
                              display="flex"
                              alignItems="center"
                              gap="0.4rem"
                            >
                              <Avatar size="2xs" name={
                                (() => {
                                  if (typeof item.miroAssignUser === "object" && item.miroAssignUser !== null) {
                                    return item.miroAssignUser.name || item.miroAssignUser.username || "Assigned";
                                  }
                                  const userObj = miroUsers.find(u => u.userId === item.miroAssignUser);
                                  return userObj ? (userObj.name || userObj.username) : "Assigned";
                                })()
                              } />
                              {(() => {
                                if (typeof item.miroAssignUser === "object" && item.miroAssignUser !== null) {
                                  return item.miroAssignUser.name || item.miroAssignUser.username || "Assigned";
                                }
                                const userObj = miroUsers.find(u => u.userId === item.miroAssignUser);
                                return userObj ? (userObj.name || userObj.username) : "Assigned";
                              })()}
                            </Badge>
                            <IconButton 
                              aria-label="Edit assignee" 
                              icon={<FiEdit2 />} 
                              size="xs" 
                              variant="ghost" 
                              colorScheme="teal"
                              onClick={(e) => openAssignModal(e, [item._id], item.processType, item.processName)}
                            />
                          </Flex>
                        ) : (
                          <Button 
                            leftIcon={<FiUserPlus />} 
                            size="sm" 
                            variant="solid" 
                            colorScheme="teal" 
                            borderRadius="full"
                            onClick={(e) => openAssignModal(e, [item._id], item.processType, item.processName)}
                            fontWeight="600"
                            boxShadow="sm"
                            _hover={{ transform: "translateY(-1px)", boxShadow: "md" }}
                          >
                            Assign User
                          </Button>
                        )}
                      </Td>
                      {dynamicColumns.map((col) => {
                        const orig = item.originalValues || {};
                        let val = "";
                        for (const alias of col.aliases) {
                          if (orig[alias] !== undefined && orig[alias] !== null && orig[alias] !== "") {
                            val = String(orig[alias]);
                            break;
                          }
                        }
                        if (!val) {
                          for (const alias of col.aliases) {
                            if (orig[alias] !== undefined && orig[alias] !== null) {
                              val = String(orig[alias]);
                              break;
                            }
                          }
                        }
                        return (
                          <Td key={col.key} color="gray.600" whiteSpace="nowrap">
                            {val !== undefined && val !== null ? val : "N/A"}
                          </Td>
                        );
                      })}
                      <Td color="gray.600" whiteSpace="nowrap">{item.plantCode || "N/A"}</Td>
                      <Td color="gray.600" whiteSpace="nowrap">{item.purchaseGroup || "N/A"}</Td>
                      <Td color="gray.500" fontSize="xs" whiteSpace="nowrap">
                        {item.updated_At ? formatDateTime(item.updated_At) : "N/A"}
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination */}
        {totalPages > 1 && (
          <Flex mt="1.5rem" justify="flex-end">
            <Pagination totalPages={totalPages} handlePageChange={handlePageChange} />
          </Flex>
        )}
      </Box>

      {/* PO Component Drawer */}
      {isDrawerOpen && selectedData && (
        <POComponent
          data={selectedData}
          file={selectedFile}
          isOpen={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setSelectedData(null);
            setSelectedFile(null);
          }}
          handleGet={fetchMiroDocuments}
          name="Miro Documents"
        />
      )}

      {/* Assign User Modal */}
      <Modal isOpen={isAssignOpen} onClose={onAssignClose} isCentered motionPreset="slideInBottom">
        <ModalOverlay backdropFilter="blur(5px)" bg="blackAlpha.300" />
        <ModalContent borderRadius="2xl" boxShadow="2xl" p={{ base: "0.5rem", md: "1rem" }} overflow="visible">
          <ModalHeader color={useColorModeValue("teal.700", "teal.300")} display="flex" alignItems="center" gap="0.75rem" pb="0">
            <Box p="0.6rem" bg={useColorModeValue("teal.50", "teal.900")} borderRadius="lg">
              <Icon as={FiUserPlus} w={5} h={5} color="teal.500" />
            </Box>
            <Text fontSize="xl" fontWeight="700">Assign User</Text>
          </ModalHeader>
          <ModalCloseButton mt="1.5rem" mr="1.5rem" borderRadius="full" />
          
          <ModalBody pt="1.5rem" pb="2rem">
            <Text mb="0.75rem" fontWeight="600" color={useColorModeValue("gray.600", "gray.400")} fontSize="xs" textTransform="uppercase" letterSpacing="wider">
              Select Team Member
            </Text>
            <Box style={{ zIndex: 100, position: 'relative' }}>
              <ReactSelect
                placeholder="Search user..."
                value={miroUsers.map(u => ({ value: u.userId, label: u.userName || u.userId })).find(opt => opt.value === selectedUserId) || null}
                onChange={(selected: any) => setSelectedUserId(selected ? selected.value : "")}
                options={miroUsers.map(user => ({
                  value: user.userId,
                  label: user.userName || user.userId
                }))}
                isClearable
                isSearchable
                styles={{
                  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                  control: (base: any, state: any) => ({
                    ...base,
                    borderRadius: '0.75rem',
                    padding: '0.25rem',
                    borderColor: state.isFocused ? '#319795' : 'var(--chakra-colors-gray-200)',
                    boxShadow: state.isFocused ? '0 0 0 1px #319795' : 'sm',
                    '&:hover': {
                      borderColor: '#4FD1C5'
                    }
                  }),
                  menu: (base: any) => ({
                    ...base,
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  }),
                  option: (base: any, state: any) => ({
                    ...base,
                    backgroundColor: state.isSelected ? '#319795' : state.isFocused ? 'var(--chakra-colors-teal-50)' : 'transparent',
                    color: state.isSelected ? 'white' : 'inherit',
                    cursor: 'pointer',
                    '&:active': {
                      backgroundColor: '#2C7A7B'
                    }
                  })
                } as any}
                menuPortalTarget={document.body}
              />
            </Box>
          </ModalBody>
          
          <ModalFooter borderTop="1px solid" borderColor={useColorModeValue("gray.100", "gray.700")} pt="1.25rem">
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={onAssignClose} 
              borderRadius="full"
              fontWeight="600"
              color={useColorModeValue("gray.500", "gray.400")}
              _hover={{ bg: useColorModeValue("gray.100", "gray.700") }}
            >
              Cancel
            </Button>
            <Button 
              colorScheme="teal" 
              borderRadius="full" 
              isLoading={assigningUser} 
              onClick={handleAssignUser}
              isDisabled={!selectedUserId}
              fontWeight="600"
              px="1.5rem"
              boxShadow="md"
              _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
              transition="all 0.2s"
            >
              Confirm Assignment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
